import React, { useEffect, useRef, useState } from 'react';
import { useToast } from '../contexts/ToastContext';
import { assetRegisterService, RegisterReport } from '../services/assetRegister';
import './AssetRegisterTransfer.css';

interface Props {
  facilityId: string;
  canEdit: boolean;
  onImported: () => void | Promise<void>;
}

/** Preview a register before applying any changes to the facility. */
export default function AssetRegisterTransfer({ facilityId, canEdit, onImported }: Props) {
  const [menu, setMenu] = useState(false);
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [report, setReport] = useState<RegisterReport | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const request = useRef(0);
  const dialog = useRef<HTMLDialogElement>(null);
  const { showSuccess, showError } = useToast();

  useEffect(() => {
    if (open) dialog.current?.showModal();
    else dialog.current?.close();
  }, [open]);
  useEffect(() => () => { request.current += 1; }, []);

  const close = () => { request.current += 1; setOpen(false); setBusy(false); setFile(null); setReport(null); setMessage(''); };
  const preview = async (selected?: File) => {
    if (!selected || busy) return;
    const sequence = ++request.current;
    setFile(selected); setReport(null); setMessage('');
    if (selected.size > 2 * 1024 * 1024) { setMessage('Choose a file no larger than 2 MB.'); return; }
    setBusy(true);
    try {
      const result = await assetRegisterService.import(facilityId, selected, true);
      if (sequence === request.current) setReport(result);
    } catch (error) {
      if (sequence === request.current) setMessage(error instanceof Error ? error.message : 'Unable to preview this file.');
    } finally { if (sequence === request.current) setBusy(false); }
  };
  const apply = async () => {
    if (!file || !report || report.errors.length || busy) return;
    setBusy(true); setMessage('');
    try {
      const result = await assetRegisterService.import(facilityId, file, false);
      setReport(result);
      if (!result.errors.length) {
        await onImported();
        close();
        showSuccess('Asset register imported', `${result.created} new · ${result.updated} updated · ${result.unchanged} unchanged`);
      }
    } catch (error) {
      setReport(null);
      setMessage(error instanceof Error ? error.message : 'Unable to import this file.');
    } finally { setBusy(false); }
  };
  const exportCsv = async () => {
    setMenu(false);
    try { await assetRegisterService.export(facilityId); }
    catch (error) { showError('Export failed', error instanceof Error ? error.message : 'Please try again.'); }
  };

  if (!canEdit) return null;
  const count = (report?.created || 0) + (report?.updated || 0);
  const previewRows = [...(report?.preview || [])].sort((a, b) => Number(b.action === 'error') - Number(a.action === 'error') || a.row - b.row);
  return <div className="asset-transfer">
    <button className="btn btn-secondary" aria-haspopup="menu" aria-expanded={menu} onClick={() => setMenu(!menu)}>Import / Export</button>
    {menu && <div className="asset-transfer__menu" role="menu" onKeyDown={event => { if (event.key === 'Escape') setMenu(false); }}>
      <button role="menuitem" onClick={exportCsv}>Export CSV</button>
      <button role="menuitem" onClick={() => { setMenu(false); setOpen(true); }}>Import from CSV…</button>
      <button role="menuitem" onClick={() => { setMenu(false); assetRegisterService.template(); }}>Download template</button>
    </div>}
    <dialog ref={dialog} className="asset-transfer__dialog" aria-labelledby="asset-import-title" onCancel={event => { event.preventDefault(); if (!busy) close(); }}>
      {open && <>
        <header><h2 id="asset-import-title">Import asset register</h2><button className="btn btn-secondary" onClick={close} disabled={busy}>Close</button></header>
        <p>Choose a CSV or Excel (.xlsx) file, up to 2 MB and 2,000 assets. The preview does not save changes. Matching names at the same location update existing assets.</p>
        <div className="asset-transfer__drop" onDragOver={event => event.preventDefault()} onDrop={event => { event.preventDefault(); void preview(event.dataTransfer.files[0]); }}>
          <label>Choose CSV or Excel file<input type="file" accept=".csv,.xlsx" disabled={busy} onChange={event => void preview(event.target.files?.[0])} /></label>
          <span>or drop a file here</span>{file && <strong>{file.name}</strong>}
        </div>
        {busy && <p role="status">Checking and processing the register…</p>}
        {message && <p role="alert">{message}</p>}
        {report && <>
          <div className="asset-transfer__summary" role="status">
            <span>{report.created} new</span> · <span>{report.updated} updated</span> · <span>{report.unchanged} unchanged</span> · <span>{report.errors.length} errors</span> · <span>{report.locations_created} new locations</span>
          </div>
          {report.errors.length > 0 && <ul className="asset-transfer__errors">{report.errors.map((error, i) => <li key={i}>Row {error.row}, {error.column.replace(/_/g, ' ')}: {error.message}</li>)}</ul>}
          {report.warnings.map((warning, i) => <p key={i}>{warning}</p>)}
          <p>Preview of the first 50 rows. Errors are shown first.</p>
          <div className="asset-transfer__table"><table><thead><tr>{['Row', 'Location', 'Name', 'Type', 'Action', 'Message'].map(h => <th key={h}>{h}</th>)}</tr></thead>
            <tbody>{previewRows.map(row => <tr key={row.row}><td>{row.row}</td><td>{row.location_path || 'No location'}</td><td>{row.name}</td><td>{row.asset_type.replace(/_/g, ' ')}</td><td><span className={`asset-transfer__pill asset-transfer__pill--${row.action}`}>{row.action[0].toUpperCase() + row.action.slice(1)}</span></td><td>{row.message}</td></tr>)}</tbody>
          </table></div>
        </>}
        <footer><button className="btn btn-primary" disabled={busy || !report || report.errors.length > 0 || count === 0} onClick={apply}>Import {count} assets</button></footer>
      </>}
    </dialog>
  </div>;
}
